import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { EXTERNAL_URLS } from '../../constants';

// Import assets
import { 
  PetPulseLogo, 
  PetPulseLogoCompact, 
  PieChartIcon, 
  ActivityIcon, 
  HelpIcon, 
  LogoutIcon 
} from '../../assets';

// Custom TTIcon component based on Fire jet design
const TTIcon = ({ 
  className = "", 
  icon, 
  container1 = "", 
  text = "Overview",
  isActive = false,
  onClick = null
}) => {
  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <div 
        className={`flex items-center gap-2.5 cursor-pointer ${isActive ? 'text-cyan-600' : 'text-neutral-700'}`}
        onClick={onClick}
      >
        <img className={`h-5 w-5 ${isActive ? '' : 'grayscale'}`} src={icon} loading="lazy" alt={text} />
        <div className={`line-clamp-[1] overflow-ellipsis text-sm ${container1} ${isActive ? 'text-cyan-600' : ''}`}>
          {text}
        </div>
      </div>
      <div className="h-px bg-neutral-700/20" />
    </div>
  );
};

// Collapsed sidebar component
const SidebarCollapsed = ({ className = "", onToggle, isMobile = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, getOrganizationType } = useAuth();
  const orgType = getOrganizationType();
  const isGovernmentOrg = orgType && orgType.toLowerCase() === 'government';
  const isPrivateOrg = orgType && orgType.toLowerCase() === 'private';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleHelpCenter = () => {
    window.open(EXTERNAL_URLS.HELP_CENTER, '_blank');
  };

  const navigation = [
    // Private clinics get the Clinic Overview page; /dashboard would only bounce
    // them there, and the active highlight below matches on the exact pathname.
    isPrivateOrg
      ? { name: 'Clinic Overview', href: '/clinic-overview', icon: PieChartIcon }
      : { name: 'Overview', href: '/dashboard', icon: PieChartIcon },
    { name: 'Clinical Intelligence', href: '/clinical-intelligence', icon: ActivityIcon, hidden: true },
    ...(isGovernmentOrg ? [{ name: 'Vaccinator Activity', href: '/vaccinator-activity', icon: EXTERNAL_URLS.VACCINATOR_ICON, hidden: true }] : []),
  ].filter(item => !item.hidden);

  const generalItems = [
    { name: 'Help Center', icon: HelpIcon },
    { name: 'Logout', icon: LogoutIcon },
  ];

  return (
    <div
      style={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: '#ffffff',
        paddingTop: '24px',
        paddingBottom: '24px',
        boxSizing: 'border-box',
      }}
    >
      {/* Logo */}
      <img
        style={{ height: '42px', width: '40px', cursor: 'pointer', flexShrink: 0 }}
        src={PetPulseLogoCompact}
        loading="lazy"
        alt="Pet Pulse Logo"
        onClick={onToggle}
      />

      {/* Main Navigation */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', marginTop: '32px' }}>
        {navigation.map((item) => (
          <Link key={item.name} to={item.href} title={item.name}>
            <img
              style={{
                height: '24px',
                width: '24px',
                opacity: location.pathname === item.href ? 1 : 0.5,
                filter: location.pathname === item.href ? 'none' : 'grayscale(100%)',
                transition: 'opacity 0.2s',
              }}
              src={item.icon}
              loading="lazy"
              alt={item.name}
            />
          </Link>
        ))}
      </div>

      {/* Spacer — pushes general items to the bottom */}
      <div style={{ flex: 1 }} />

      {/* General Items — always visible at bottom */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
        {generalItems.map((item) => (
          <button
            key={item.name}
            title={item.name}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              opacity: 0.6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '0.6')}
            onClick={
              item.name === 'Logout'
                ? handleLogout
                : item.name === 'Help Center'
                ? handleHelpCenter
                : undefined
            }
          >
            <img
              style={{ height: '24px', width: '24px' }}
              src={item.icon}
              loading="lazy"
              alt={item.name}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

// Expanded sidebar component
const SidebarExpanded = ({ className = "", onToggle, isMobile = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, getOrganizationType } = useAuth();
  const orgType = getOrganizationType();
  const isGovernmentOrg = orgType && orgType.toLowerCase() === 'government';
  const isPrivateOrg = orgType && orgType.toLowerCase() === 'private';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleHelpCenter = () => {
    window.open(EXTERNAL_URLS.HELP_CENTER, '_blank');
  };

  const navigation = [
    // Private clinics get the Clinic Overview page; /dashboard would only bounce
    // them there, and the active highlight below matches on the exact pathname.
    isPrivateOrg
      ? { name: 'Clinic Overview', href: '/clinic-overview', icon: PieChartIcon }
      : { name: 'Overview', href: '/dashboard', icon: PieChartIcon },
    { name: 'Clinical Intelligence', href: '/clinical-intelligence', icon: ActivityIcon, hidden: true },
    ...(isGovernmentOrg ? [{ name: 'Vaccinator Activity', href: '/vaccinator-activity', icon: EXTERNAL_URLS.VACCINATOR_ICON, hidden: true }] : []),
  ].filter(item => !item.hidden);

  const generalItems = [
    { name: 'Help Center', icon: HelpIcon },
    { name: 'Logout', icon: LogoutIcon },
  ];

  return (
    <div className={`flex h-screen w-full flex-col gap-2.5 bg-white px-4 pb-6 pt-4 leading-[normal] ${className}`}>
      {/* Logo + (mobile) close button */}
      <div className="flex items-start justify-between">
        <img
          className="h-20 w-auto cursor-pointer object-contain object-left"
          src={PetPulseLogo}
          loading="lazy"
          alt="Pet Pulse Dashboard"
          onClick={onToggle}
        />
        {isMobile && (
          <button
            type="button"
            onClick={onToggle}
            aria-label="Close menu"
            className="mt-1 -mr-1 p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
      </div>
      
      <div className="font-poppins flex flex-col gap-[50px]">
        {/* Analytical Dashboard Section */}
        <div className="flex flex-col gap-2.5">
          <div className="text-[11px] uppercase leading-4 text-gray-500">
            ANALYTICAL DASHBOARD
          </div>
          <div className="flex flex-col justify-end bg-white px-4 pt-[18px]">
            <div className="flex items-center justify-end pl-2">
              <div className="flex flex-grow flex-col gap-[18px] font-medium leading-[22px]">
                {navigation.map((item) => (
                  <Link key={item.name} to={item.href}>
                    <TTIcon
                      icon={item.icon}
                      container1={location.pathname === item.href ? "w-40 text-cyan-600" : "w-40 text-neutral-700"}
                      text={item.name}
                      isActive={location.pathname === item.href}
                    />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* General Section */}
        <div className="flex flex-col gap-2.5">
          <div className="text-[11px] uppercase leading-4 text-gray-500">GENERAL</div>
          <div className="flex flex-col justify-end bg-white px-4 pt-[18px]">
            <div className="flex items-center justify-end pl-2">
              <div className="flex flex-grow flex-col gap-[18px] font-medium leading-[22px] text-neutral-700">
                {generalItems.map((item) => (
                  <TTIcon
                    key={item.name}
                    icon={item.icon}
                    container1={item.name === 'Logout' ? "w-40 text-red-500" : "w-40"}
                    text={item.name}
                    onClick={
                      item.name === 'Logout' 
                        ? handleLogout 
                        : item.name === 'Help Center' 
                        ? handleHelpCenter 
                        : undefined
                    }
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main FireJetSidebar component
const FireJetSidebar = ({ className = "", isExpanded = true, onToggle, isMobile = false }) => {
  return (
    <div className={`fixed inset-y-0 left-0 z-50 ${
      isExpanded ? 'w-64' : 'w-20'
    } transition-all duration-300 ${
      isMobile 
        ? (isExpanded ? 'translate-x-0' : '-translate-x-full') 
        : ''
    } ${className}`}>
      {isExpanded ? (
        <SidebarExpanded onToggle={onToggle} isMobile={isMobile} />
      ) : (
        <SidebarCollapsed onToggle={onToggle} isMobile={isMobile} />
      )}
    </div>
  );
};

export default FireJetSidebar;